<template>
  <a-modal :centered="true" v-model="showModal" :footer="null" @cancel="hide" width="587px" class="mobile-modal-fullscreen editQuestion quill-css-mob" :closable="false">
    <template v-if="currentScreen === 'edit'">
      <div class="d-flex align-items-center mob-shadow">
        <font-awesome-icon size="2x" :icon="['fal', 'times']" class="show-in-mobile font-black mr-3 font20" @click="hide" />
        <h4 class="fw700 font24 gray700 mb-0">
          {{ !editing ? "Ask a" : "Edit" }}
          Question
        </h4>
        <div class="saveBtn ml-auto br6 p1224 font-white font14 fw700 cp show-in-mobile" :style="{ background: getPrimaryColor() }" @click="edit">
          {{ posting ? "Loading..." : !editing ? "Ask" : "Save" }}
        </div>
        <font-awesome-icon size="2x" :icon="['fal', 'times']" class="hide-in-mobile font-black ml-auto cp font20" @click="hide" />
      </div>

      <div class="d-flex align-items-center my-4">
        <img v-if="authUser.user.url" class="hw40" :src="authUser.user.url" />
        <div v-else class="hw40 flex-center-center fw700 font16 text-white rounded-circle ar1" :style="{ background: getAvatarColor(authUser.user.id) }">
          {{ getAuthUserInitials() }}
        </div>
        <div class="d-flex align-items-center flex-wrap">
          <p class="fw700 font14 gray900 ml-2 mb-0 text-capitalize">
            {{ authUser.user.display_name }}
          </p>
          <div class="d-flex">
            <!-- QUESTION TAG -->
            <div class="ml-1 p-2 bg-gray100 rounded font14 gray900">
              <font-awesome-icon :icon="['fal', 'question-circle']" class="mr-1" :style="{ color: getPrimaryColor() }" />
              Asked a question
            </div>
            <!-- CATEGORY TAG -->
            <div class="ml-1 p-2 bg-gray100 rounded font14 gray900">
              {{ selectedCategory }}
              <font-awesome-icon :icon="['fal', 'exchange']" class="ml-2 cp gray900" @click="changeScreen('categorys')" />
            </div>
          </div>
        </div>
      </div>

      <div class="position-relative">
        <quillEditor v-model="questionDescription" ref="quillEditor" :options="editorOptions" class="createpost-quill" />
        <CustomEmojiPicker class="position-absolute editor-emoji" :visible="emojiVisible" :handleVisible="handleEmojiVisible" :insert="insert" :customColor="'#94a3b8 !important'" />
        <a-divider
          type="vertical"
          class="position-absolute hide-in-mobile"
          :style="{
            left: '50px',
            bottom: '7.5px',
            height: '24px',
            top: 'auto',
          }"
        />
        <div id="counter" class="position-absolute">0/5000</div>
      </div>

      <!-- ADDING IMAGES -->
      <template v-if="addingImages">
        <div v-if="isImageUploading" class="d-flex justify-content-center mt-2">
          <div>
            <div class="spinner-border position-absolute spinner-class"></div>
          </div>
          <div :style="{ 'margin-left': '-33px', 'margin-top': '40px' }">Loading image...</div>
        </div>
        <template v-else-if="!question_attach.length && !isImageUploading">
          <div class="imageContainer mt-3">
            <font-awesome-icon
              :icon="['far', 'times']"
              class="gray500 position-absolute bg-gray100 p-1 ar1 font20 rounded-circle"
              :style="{ top: '10px', right: '10px', 'z-index': 6 }"
              @click="handleCancelImage"
            />
            <font-awesome-icon :icon="['far', 'plus']" :style="{ color: getPrimaryColor() }" />
            <p class="my-1 fw700 font14">Add Photos/Videos</p>
            <p class="gray500 font12 mb-0">or drag and drop</p>
            <input class="h-100 w-100 camera-icon-inp" type="file" accept="image/png, image/jpeg, image/gif, image/svg+xml" ref="fileInput" @change="handleImageUpload()" :style="{ right: 0 }" />
          </div>
          <p class="mb-0 gray600 font12">
            Please don’t upload images that may cause others disconfort.
            <a :href="`${SSO_BASE_URL}/help-center/faq/mycircles-privacy-policy`" target="_blank" class="gray600 font12" :style="{ color: getPrimaryColor() }"> See Community Guidelines. </a>
          </p>
        </template>
        <div class="position-relative multiImgs" v-else>
          <p class="mb-0 fw700 font14 gray900 py-1 px-2 bg-gray100 br8 position-absolute cp font16" :style="{ top: '8px', left: '8px', 'z-index': 2 }">
            <font-awesome-icon :icon="['fal', 'images']" class="gray900 mr-1" />
            Add more images
            <input class="h-100 w-100 camera-icon-inp" type="file" accept="image/png, image/jpeg, image/gif, image/svg+xml" ref="fileInput" @change="handleImageUpload()" :style="{ right: 0 }" />
          </p>
          <p
            class="mb-0 fw700 font14 gray900 bg-gray100 br8 position-absolute cp"
            :style="{
              top: '8px',
              left: '180px',
              'z-index': 2,
              padding: '5.5px 0.5rem',
            }"
            @click="changeScreen('pictures')"
          >
            Edit
          </p>
          <font-awesome-icon
            :icon="['far', 'times']"
            class="gray500 position-absolute bg-gray100 p-1 ar1 font20 rounded-circle cp"
            :style="{ top: '16px', right: '16px', 'z-index': 10 }"
            @click="handleCancelImage"
          />
          <template v-if="question_attach.length">
            <div v-for="(imgFile, ind) in question_attach" :key="imgFile" :class="`position-relative ${getClass(ind)}`">
              <img :src="imgFile.url" />
              <div v-if="ind === 2 && question_attach.length > 3" class="more" @click="changeScreen('pictures')"></div>
              <span v-if="ind === 2 && question_attach.length > 3" class="moreText"> +{{ question_attach.length - 2 }} </span>
            </div>
          </template>
        </div>
      </template>

      <!-- TAGS -->
      <div class="d-flex flex-wrap mt-4" v-if="selectedTags.length">
        <div class="rounded p-2 bg-gray50 d-flex align-items-center fw700 gray500 font14 mr-2 mb-2" v-for="tag in selectedTags" :key="tag">
          {{ tag }}
          <font-awesome-icon :icon="['fas', 'trash']" class="cp error300 ml-2" @click="handleDeleteTag(tag)" />
        </div>
      </div>

      <p class="fw700 font16 mb-0 gray500 mt-4">Add to your post</p>

      <div class="d-flex mt-4 justify-content-between">
        <a-tooltip
          v-for="option in optionList"
          :key="option.name"
          :class="`rounded p-2 d-flex flex-column align-items-center ${option.disabled ? 'noHover' : 'cp'}`"
          :style="{
            background: option.selected ? getPrimaryColor() : '#F8FAFD',
            width: '100px',
          }"
          @click="() => !option.disabled && changeScreen(option.screen)"
        >
          <template #title>
            {{ option.disabled ? "This can’t be combined with what you’ve already added to your post." : option.description }}
          </template>
          <font-awesome-icon
            :style="{
              color: option.disabled ? '#94A3B8' : option.selected ? '#FFFFFF' : getPrimaryColor(),
            }"
            :icon="[option.selected ? 'fas' : 'fal', option.icon]"
            class="mb-1"
          />
          <span :class="`font10 ${option.disabled ? 'gray400' : option.selected ? 'font-white' : 'gray900'}`">
            {{ option.name }}
          </span>
        </a-tooltip>
      </div>

      <div class="mt-4 hide-in-mobile" :style="{ display: 'flex' }">
        <div class="bg-gray200 br6 fw700 font14 gray600 cp p1224 ml-auto" @click="hide">Cancel</div>
        <div class="br6 fw700 font14 text-white cp p1224 ml-3" :style="{ background: getPrimaryColor() }" @click="edit">
          {{ posting ? "Loading..." : !editing ? "Ask" : "Save" }}
        </div>
      </div>
    </template>

    <CategoriesScreen v-else-if="currentScreen === 'categorys'" :changeScreen="changeScreen" :selectedCategory="selectedCategory"/>

    <TagsScreen v-else-if="currentScreen === 'tags'" :changeScreen="changeScreen" :prevSelectedTags="selectedTags" :hide="hide" />

    <AllPicturesScreen v-else-if="currentScreen === 'pictures'" :getImageData="getImageData" :changeScreen="changeScreen" :ImagesArrayProp="question_attach" />

    <MoreOptionsScreen
      v-else-if="currentScreen === 'moreOptions'"
      :changeScreen="changeScreen"
      :selectedOptions="['question', `${selectedTags.length ? 'tags' : ''}`]"
      :disabledOptions="['pressRelease', 'event', 'job', 'image', 'feelings', 'celebrations', 'poll']"
      :hide="hide"
    />
  </a-modal>
</template>

<script>
import "quill/dist/quill.core.css"
import "quill/dist/quill.snow.css"
import Quill from "quill"
import { quillEditor } from "vue-quill-editor"
import AllPicturesScreen from "../../home/createNewPost/Pictures.vue"
import TagsScreen from "../../home/createNewPost/Tags.vue"
import CustomEmojiPicker from "../../home/emojiPicker.vue"
import s3Upload from "../../../utilities/imageUploader"
import { RepositoryFactory } from "../../../repositories/RepositoryFactory"
import MoreOptionsScreen from "../../home/createNewPost/MoreOptions.vue"
import CategoriesScreen from "../../home/createNewPost/Categories.vue"

Quill.register("modules/counter", function (quill) {
  var container = document.querySelector("#counter")
  quill.on("text-change", function () {
    var text = quill.getText()
    container.innerText = `${parseInt(text.length) - 1}/5000`
  })
})

const PostRepository = RepositoryFactory.get("posts")
const UserRepository = RepositoryFactory.get("user")

export default {
  name: "EditQuestion",
  props: {
    userName: String,
    fetchPosts: Function,
    render: Boolean,
  },
  components: { quillEditor, CustomEmojiPicker, CategoriesScreen, AllPicturesScreen, TagsScreen, MoreOptionsScreen },
  data() {
    const placeholder = `What's on your mind, ${this.userName}?`
    return {
      descriptionLength: 0,
      posting: false,
      showModal: false,
      currentScreen: "edit",
      questionDescription: "",
      editing: true,
      emojiVisible: false,
      isImageUploading: false,
      selectedTags: [],
      addingImages: false,
      questionId: 0,
      selectedCategory: "General",
      question_attach: [],
      optionList: [
        {
          name: "Image/video",
          disabled: false,
          selected: false,
          icon: "image",
          screen: "image",
          description: "Add Images",
        },
        {
          screen: "feelings",
          name: "Feeling",
          selected: false,
          disabled: true,
          icon: "comment-alt-smile",
          description: "Add Feeling",
        },
        {
          name: "Tags",
          selected: false,
          screen: "tags",
          disabled: false,
          icon: "tag",
          description: "Add Tags",
        },
        {
          name: "Ask",
          selected: true,
          screen: "question",
          disabled: false,
          icon: "question-circle",
          description: "Mark as Question",
        },
        {
          name: "More options",
          screen: "moreOptions",
          disabled: false,
          selected: false,
          icon: "ellipsis-v",
          description: "See more options",
        },
      ],
      editorOptions: {
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
          ],
          counter: true,
        },
        placeholder,
      },
    }
  },
  computed: {
    SSO_BASE_URL: () => process.env.SSO_BASE_URL
  },
  methods: {
    getImageData(v) {
      // console.log("getImageData called")
      this.currentScreen = "edit"
      this.question_attach = v || []
    },
    getClass(i) {
      switch (i) {
        case 0:
          return "first"
        case 1:
          return "second"
        case 2:
          return "third"
        default:
          return ""
      }
    },
    handleDeleteTag(val) {
      this.selectedTags = this.selectedTags.filter(t => t !== val)
      if (!this.selectedTags.length) {
        this.changeScreen("edit")
      }
    },
    handleCancelImage() {
      this.addingImages = false
      this.changeScreen("edit")
    },
    changeScreen(screen, type, value) {
      // console.log("changed screen to ->", screen)
      switch (screen) {
        case "edit":
        case "add":
        case "back":
        case "question":
          this.currentScreen = "edit"
          break
        case "image":
          this.addingImages = true
          break
        default:
          this.currentScreen = screen
          break
      }
      if (type === "category") {
        this.selectedCategory = value
      } else if (type === "tag") {
        this.selectedTags = value
      }
      this.optionList = this.optionList.map(o => {
        if ("image" === o.screen) {
          return { ...o, selected: this.addingImages }
        } else if ("tags" === o.screen) {
          return { ...o, selected: this.selectedTags.length }
        } else {
          return o
        }
      })
    },
    show(oldData, treatment = false, askQ = false) {
      this.showModal = true
      if (askQ) {
        this.editing = false
        return
      }
      if (treatment) {
        this.editing = false
        this.selectedCategory = "Treatment"
        return
      }
      // console.log("oldData ->", oldData)
      this.questionDescription = oldData.question_description || ""
      this.selectedCategory = oldData.category || "General"
      this.question_attach = oldData.question_attach_url || []
      this.addingImages = oldData.question_attach_url ? oldData.question_attach_url.length : false
      this.selectedTags = oldData.tags || []
      this.questionId = oldData.id || null
      // console.log("q Id ->", this.questionId)
      this.changeScreen("edit")
    },
    hide() {
      this.showModal = false
      this.emojiVisible = false
      this.questionDescription = ""
      this.selectedCategory = "General"
      this.question_attach = []
      this.selectedTags = []
      this.currentScreen = "edit"
    },
    edit() {
      if (this.posting) {
        return
      }
      this.posting = true
      const payload = {
        user_circle: this.currentCircle.id,
        category: this.selectedCategory,
        question: "",
        question_description: this.questionDescription,
        question_attach: this.question_attach.length ? this.question_attach.map(i => ({ name: i.name, sensitive: i.sensitive })) : [],
        // question_attach: this.question_attach.length ? this.question_attach : [],
        question_location: "location",
        latlong: null,
        user: this.authUser.user.id,
        tags: this.selectedTags,
      }
      const headers = {
        Authorization: `Bearer ${this.authUser.user.token}`,
      }
      if (this.questionDescription.trim().length && this.$refs.quillEditor.quill.getText().trim().length) {
        if (!this.editing) {
          PostRepository.createQuestion(payload)
            .then(res => {
              // this.getPost()
              if (this.render) {
                this.fetchPosts()
              }
              this.$notification.success({
                message: "Success",
                description: "Question created successfully.",
              })
              this.posting = false
              this.hide()
            })
            .catch(err => {
              this.$notification.error({
                message: "Error",
                description: "Question was not created.",
              })
              this.posting = false
            })
        } else {
          this.$http
            .put(`${process.env.BASE_API_URL}/askquestions/?id=${this.questionId}`, payload, {
              headers,
            })
            .then(res => {
              this.fetchPosts()
              this.$notification.success({
                message: "Success",
                description: "Your question has been Edited Successfully.",
              })
              this.posting = false
              // this.$router.go();
              // this.hide();
              this.showModal = false
            })
            .catch(err => {
              this.posting = false
              this.$notification.error({
                message: "Error",
                description: "Your post could not be edited",
              })
            })
        }
      }
    },
    insert(emoji) {
      if (this.questionDescription[this.questionDescription.length - 2] === "l") {
        this.questionDescription = this.questionDescription.substring(0, this.questionDescription.length - 10) + emoji + this.questionDescription.substring(this.questionDescription.length - 10)
      } else {
        this.questionDescription = this.questionDescription.substring(0, this.questionDescription.length - 4) + emoji + this.questionDescription.substring(this.questionDescription.length - 4)
      }
      this.handleEmojiVisible()
      setTimeout(() => {
        this.$refs.quillEditor.quill.blur()
      }, 10)
    },
    handleEmojiVisible() {
      this.emojiVisible = !this.emojiVisible
    },
    async handleImageUpload() {
      const file = this.$refs.fileInput.files[0]
      this.isImageUploading = true
      try {
        if (file.size / (1024 * 1024) > 5) {
          this.$notification.error({
            message: "Error",
            description: "File size exceeds 5MB limit",
          })
          const input = this.$refs.file
          input.type = "text"
          input.type = "file"
        } else if (!file.type.includes("image")){
            this.$notification.error({
              message: "Error",
              description: "Please select only image"
            });
        } else {
          const urlPayload = { file_name: [file.name] }
          const urlData = await UserRepository.getPresignedUrl(urlPayload)
          const fileName = urlData.data.data.filename[0]
          await s3Upload(urlData.data.data.url[0], file)
          const fileListData = {
            // uid: this.question_attach.length,
            // name: file.name,
            name: fileName,
            // status: "done",
            url: urlData.data.data.preview_url,
            sensitive: false,
          }
          this.question_attach.push(fileListData)
        }
      } catch (err) {
        this.$notification.error({
          message: "Error",
          description: "Image upload failed",
        })
      }
      this.isImageUploading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.editor-emoji {
  left: 10px;
  bottom: 5px;
  @media screen and (max-width: 600px) {
    bottom: auto;
    top: 5px;
  }
}
.rounded.cp {
  &:hover {
    background: #cbd5e1 !important;
  }
}
.noHover:hover {
  background: #f8fafd !important;
}
@media screen and (max-width: 600px) {
  .justify-content-between {
    gap: 8px;
    span:nth-child(4) {
      display: none !important;
    }
  }
}
.imageContainer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 12px 24px;
  background: #f8fafd;
  border: 1px dashed #94a3b8;
  border-radius: 6px;
  cursor: pointer;
  min-height: 171px;
  position: relative;
}
#counter {
  right: 0;
  bottom: 10px;
  @media screen and (max-width: 600px) {
    right: 16px;
    bottom: auto;
    top: 10px;
  }
}
.multiImgs {
  display: grid;
  grid-template-areas: "first first" "first first" "second third";
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: white;
    display: none;
  }
  .first {
    grid-area: first;
    max-height: 360px;
  }
  .second {
    grid-area: second;
  }
  .third {
    grid-area: third;
  }
  .first,
  .second,
  .third {
    img {
      display: block !important;
    }
  }
  .more {
    background: #0f1734 !important;
    opacity: 0.6;
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    cursor: pointer;
  }
  .moreText {
    cursor: pointer;
    font-weight: 700;
    position: absolute;
    font-size: 24px;
    line-height: 24px;
    color: #ffffff !important;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Modals/activity/EditQuestion.vue